<template>
  <div
    :id="uuid"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
    }"
    class="relative"
  >
    <CoverContent v-if="!hasResponsiveOverrides" :form-data="formData" />
    <template v-else>
      <CoverResponsiveView
        :form-data="mobileView"
        visibility-class="sm:hidden"
      />
      <CoverResponsiveView
        :form-data="mediumView"
        visibility-class="hidden sm:block lg:hidden"
      />
      <CoverResponsiveView
        :form-data="desktopView"
        visibility-class="hidden lg:block"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  CoverDisplayData,
  CoverFormData,
  CoverImageRatioMode,
  CoverLogoAlign,
  CoverPaddingSize,
  CoverTextAlign,
} from './types'
import CoverContent from './CoverContent.vue'
import CoverResponsiveView from './CoverResponsiveView.vue'
import { usePageEditor } from '~/stores/page-editor'

interface PropsInterface {
  uuid: string
  formData: CoverFormData
}

interface ResponsiveSettings {
  isEnabled: boolean
  hasBgImage: boolean
  bgImage?: string
  hasBgColor: boolean
  bgColor?: string
  imageRatioMode?: CoverImageRatioMode
  paddingSize?: CoverPaddingSize
  logoWidth?: number
  logoAlign?: CoverLogoAlign
  maxWidth?: number
  textAlign?: CoverTextAlign
}

const props = defineProps<PropsInterface>()
const pageEditorStore = usePageEditor()

const desktopView = computed<CoverDisplayData>(() => props.formData)

const hasResponsiveOverrides = computed(
  () =>
    props.formData.overrideMobileView === true ||
    props.formData.overrideMediumView === true
)

const hasOwnProperty = (property: keyof CoverFormData) =>
  Object.prototype.hasOwnProperty.call(props.formData, property)

const resolveImageRatio = (mode?: CoverImageRatioMode) => {
  if (mode === 'enabled') {
    return true
  }

  if (mode === 'disabled') {
    return false
  }

  return props.formData.useImageRatio ?? false
}

const resolveResponsiveView = (
  settings: ResponsiveSettings
): CoverDisplayData => {
  if (!settings.isEnabled) {
    return props.formData
  }

  let bgImage = props.formData.bgImage
  let bgColor = props.formData.bgColor

  if (settings.hasBgImage) {
    bgImage = settings.bgImage || undefined
    bgColor = settings.bgColor ?? bgColor
  } else if (settings.hasBgColor) {
    bgImage = undefined
    bgColor = settings.bgColor
  }

  return {
    ...props.formData,
    bgImage,
    bgColor,
    useImageRatio: resolveImageRatio(settings.imageRatioMode),
    paddingSize:
      props.formData.useImageRatio === true &&
      settings.imageRatioMode === 'disabled'
        ? settings.paddingSize ?? props.formData.paddingSize
        : props.formData.paddingSize,
    logoWidth: settings.logoWidth ?? props.formData.logoWidth,
    logoAlign: settings.logoAlign ?? props.formData.logoAlign,
    maxWidth: settings.maxWidth ?? props.formData.maxWidth,
    textAlign: settings.textAlign ?? props.formData.textAlign,
  }
}

const mobileView = computed<CoverDisplayData>(() =>
  resolveResponsiveView({
    isEnabled: props.formData.overrideMobileView === true,
    hasBgImage: hasOwnProperty('mobileBgImage'),
    bgImage: props.formData.mobileBgImage,
    hasBgColor: hasOwnProperty('mobileBgColor'),
    bgColor: props.formData.mobileBgColor,
    imageRatioMode: props.formData.mobileImageRatioMode,
    paddingSize: props.formData.mobilePaddingSize,
    logoWidth: props.formData.mobileLogoWidth,
    logoAlign: props.formData.mobileLogoAlign,
    maxWidth: props.formData.mobileMaxWidth,
    textAlign: props.formData.mobileTextAlign,
  })
)

const mediumView = computed<CoverDisplayData>(() =>
  resolveResponsiveView({
    isEnabled: props.formData.overrideMediumView === true,
    hasBgImage: hasOwnProperty('mediumBgImage'),
    bgImage: props.formData.mediumBgImage,
    hasBgColor: hasOwnProperty('mediumBgColor'),
    bgColor: props.formData.mediumBgColor,
    imageRatioMode: props.formData.mediumImageRatioMode,
    paddingSize: props.formData.mediumPaddingSize,
    logoWidth: props.formData.mediumLogoWidth,
    logoAlign: props.formData.mediumLogoAlign,
    maxWidth: props.formData.mediumMaxWidth,
    textAlign: props.formData.mediumTextAlign,
  })
)
</script>
